import React, {useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button, Input, Modal, Table, Tag, message} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {DEBUG_BCP_CHECK_JOB} from '../graphql/mutations/bcpCheckJobMutations';
import {
    CheckResultInfoType,
    DebugBcpCheckResponseType,
    RecordResultType,
} from '../graphql/types/BcpCheckJobTypes';

const {TextArea} = Input;

interface Props {
    jobId: number;
    open: boolean;
    onClose: () => void;
}

const recordColumns: ColumnsType<RecordResultType> = [
    {title: '表名', dataIndex: 'tableName', key: 'tableName', width: 120},
    {title: '关联键', dataIndex: 'relatedKey', key: 'relatedKey', width: 120},
    {
        title: '阶段', dataIndex: 'status', key: 'status', width: 100,
        render: (s: string) => {
            const color = s === 'PASSED' ? 'green' : s === 'FILTERED' ? 'orange' : 'red';
            return <Tag color={color}>{s}</Tag>;
        },
    },
    {
        title: '字段快照', dataIndex: 'fields', key: 'fields', width: 300,
        render: (f: string) => f || '-',
    },
];

const checkResultColumns: ColumnsType<CheckResultInfoType> = [
    {title: '关联键', dataIndex: 'relatedKey', key: 'relatedKey', width: 120},
    {
        title: '结果码', dataIndex: 'resultCode', key: 'resultCode', width: 100,
        render: (s: string) => {
            const color = s === 'SUCCESS' ? 'green' : 'red';
            return <Tag color={color}>{s}</Tag>;
        },
    },
    {title: '失败原因', dataIndex: 'failReason', key: 'failReason', width: 200},
    {
        title: '核对数据', dataIndex: 'data', key: 'data', width: 400,
        render: (d: string) => d || '-',
    },
];

const BcpCheckJobDebugModal: React.FC<Props> = ({jobId, open, onClose}) => {
    const [rawJson, setRawJson] = useState('');
    const [result, setResult] = useState<DebugBcpCheckResponseType | null>(null);
    const [debugJob, {loading}] = useMutation(DEBUG_BCP_CHECK_JOB);

    const handleExecute = async () => {
        if (!rawJson.trim()) {
            message.warning('请输入待调试的 JSON 报文');
            return;
        }
        try {
            const res = await debugJob({variables: {input: {jobId, rawJson}}});
            const debugResult = res.data?.debugBcpCheckJob;
            if (!debugResult?.success) {
                message.error(debugResult?.message || '调试执行失败');
                return;
            }
            setResult(debugResult.data || null);
            message.success('调试执行成功');
        } catch (e: any) {
            message.error(e?.message || '调试执行失败');
        }
    };

    const handleClose = () => {
        setRawJson('');
        setResult(null);
        onClose();
    };

    return (
        <Modal
            title="调试核对任务"
            open={open}
            onCancel={handleClose}
            width={1000}
            footer={null}
            destroyOnClose
        >
            <div className="mb-4">
                <div className="text-sm font-medium mb-1">原始 JSON 报文</div>
                <TextArea
                    rows={8}
                    value={rawJson}
                    onChange={(e) => setRawJson(e.target.value)}
                    placeholder='{"trackingNumber": "SF1234567890", ...}'
                />
            </div>
            <div className="mb-4">
                <Button type="primary" onClick={handleExecute} loading={loading}>
                    执行调试
                </Button>
            </div>
            {result && (
                <div>
                    {result.records && result.records.length > 0 && (
                        <div className="mb-4">
                            <div className="text-sm font-medium mb-1">记录处理结果</div>
                            <Table
                                dataSource={result.records}
                                columns={recordColumns}
                                rowKey={(r, i) => `${r.tableName}-${r.relatedKey}-${i}`}
                                pagination={false}
                                size="small"
                                scroll={{x: 700}}
                            />
                        </div>
                    )}
                    {result.checkResults && result.checkResults.length > 0 && (
                        <div>
                            <div className="text-sm font-medium mb-1">核对结果</div>
                            <Table
                                dataSource={result.checkResults}
                                columns={checkResultColumns}
                                rowKey={(r, i) => `${r.relatedKey}-${i}`}
                                pagination={false}
                                size="small"
                                scroll={{x: 900}}
                            />
                        </div>
                    )}
                    {(!result.records || result.records.length === 0) &&
                        (!result.checkResults || result.checkResults.length === 0) && (
                            <div className="text-gray-400 text-sm">无结果数据</div>
                        )}
                </div>
            )}
        </Modal>
    );
};

export default BcpCheckJobDebugModal;
